import { z } from 'zod';
import { CatalogSeriesNextSchema, CodeExplanationSchema } from './codeKey.js';

/** Genormaliseerd Unit4-product (subset van de ~80 velden). */
export const Unit4ProductSchema = z.object({
  id: z.string().nullable().optional(),
  productCode: z.string(),
  description: z.string().nullable().optional(),
  searchName: z.string().nullable().optional(),
  unitCode: z.string().nullable().optional(),
  eanCode: z.union([z.string(), z.number()]).nullable().optional(),
  productGroupCode: z.string().nullable().optional(),
  referenceJournalEntryCode: z.string().nullable().optional(),
  costPrice: z.number().nullable().optional(),
  blocked: z.boolean().nullable().optional(),
  inactive: z.boolean().nullable().optional(),
  discontinued: z.boolean().nullable().optional(),
  isAssembly: z.boolean().nullable().optional(),
  compositionType: z.union([z.string(), z.number()]).nullable().optional(),
  assemblyInstruction: z.string().nullable().optional(),
  salesPriceBasedOnComponents: z.boolean().nullable().optional(),
  /** ruwe payload zoals uit Unit4 (voor snapshot/template) */
  raw: z.record(z.unknown()).optional(),
});
export type Unit4Product = z.infer<typeof Unit4ProductSchema>;

/** Genormaliseerde ProductCompositions-regel (let op: Unit4 spelt `precentageLoss` verkeerd — mapping in backend). */
export const Unit4CompositionLineSchema = z.object({
  id: z.string().nullable(),
  parentProductCode: z.string(),
  parentUnitCode: z.string().nullable(),
  lineNumber: z.number().nullable(),
  componentProductCode: z.string(),
  componentUnitCode: z.string().nullable(),
  componentDescription: z.string().nullable(),
  componentSearchName: z.string().nullable(),
  qtyInComposition: z.number(),
  compositionPer: z.number(),
  percentageLoss: z.number(),
  raw: z.record(z.unknown()).optional(),
});
export type Unit4CompositionLine = z.infer<typeof Unit4CompositionLineSchema>;

/** Snapshot zoals opgeslagen in `products.unit4_snapshot`. */
export const Unit4SnapshotSchema = z.object({
  product: Unit4ProductSchema,
  compositions: z.array(Unit4CompositionLineSchema),
  fetchedAt: z.string(),
});
export type Unit4Snapshot = z.infer<typeof Unit4SnapshotSchema>;

export const Unit4LoadBodySchema = z.object({
  product_code: z.string().trim().min(1).max(50),
  unit_code: z.string().trim().max(10).optional().nullable(),
});
export type Unit4LoadBody = z.infer<typeof Unit4LoadBodySchema>;

export const Unit4SearchQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  code: z.string().trim().max(50).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(25),
});

// ---------- Push ----------

export const PushOperationKindSchema = z.enum([
  'create_product',
  'update_product',
  'create_composition',
  'update_composition',
  'delete_composition',
  'upsert_note',
  'delete_note',
  'create_article',
  'create_supplier',
]);
export type PushOperationKind = z.infer<typeof PushOperationKindSchema>;

export const PushOperationSchema = z.object({
  seq: z.number().int(),
  kind: PushOperationKindSchema,
  productId: z.number().int(),
  lineId: z.number().int().nullable().optional(),
  endpoint: z.string(),
  method: z.enum(['POST', 'PUT', 'DELETE']),
  targetId: z.string().nullable().optional(),
  payload: z.record(z.unknown()).nullable(),
  description: z.string(),
  status: z.enum(['pending', 'skipped', 'ok', 'failed']).default('pending'),
  result: z.unknown().optional(),
  error: z.string().nullable().optional(),
});
export type PushOperation = z.infer<typeof PushOperationSchema>;

export const PushPlanSchema = z.object({
  productId: z.number().int(),
  env: z.enum(['test', 'prod']),
  dryRun: z.boolean(),
  order: z.array(z.number().int()),
  allocatedCodes: z.record(z.string()),
  operations: z.array(PushOperationSchema),
});
export type PushPlan = z.infer<typeof PushPlanSchema>;

export const PushResultSchema = z.object({
  ok: z.boolean(),
  plan: PushPlanSchema,
  syncLogId: z.number().int().nullable(),
  message: z.string(),
});
export type PushResult = z.infer<typeof PushResultSchema>;

export const PushBodySchema = z.object({
  confirm: z.literal(true),
});

/** Analyse van een codereeks in de catalogus-cache (voor de productcode-samensteller). */
export const CatalogSeriesLengthSchema = z.object({
  len: z.number().int(),
  count: z.number().int(),
  highest: z.string().nullable(),
  /** Volgende vrije code in deze reeks (null als de reeks vol is of niet numeriek). */
  suggestion: z.string().nullable(),
});
/** Het Unit4-artikel dat precies deze code draagt (uit de catalogus-cache), als de prefix een bestaande code is. */
export const CatalogSeriesExactSchema = z.object({
  productCode: z.string(),
  unitCode: z.string().nullable(),
  description: z.string().nullable(),
  isAssembly: z.boolean(),
  blocked: z.boolean(),
  inactive: z.boolean(),
  discontinued: z.boolean(),
});
export type CatalogSeriesExact = z.infer<typeof CatalogSeriesExactSchema>;

/** Klingerator-product dat deze code al draagt (als Unit4-code of als voorgestelde code). */
export const CatalogSeriesLocalSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  /** true = staat al in Unit4 (unit4_product_code); false = alleen voorgesteld */
  inUnit4: z.boolean(),
});
export type CatalogSeriesLocal = z.infer<typeof CatalogSeriesLocalSchema>;

export const CatalogSeriesSchema = z.object({
  prefix: z.string(),
  total: z.number().int(),
  lengths: z.array(CatalogSeriesLengthSchema),
  examples: z.array(z.object({ productCode: z.string(), description: z.string().nullable() })),
  /** Uitleg van de prefix volgens de codesleutel (null bij een niet-numerieke prefix) */
  explanation: CodeExplanationSchema.nullable(),
  /** Mogelijke volgende posities: sleutel-kinderen én cijfers die alleen in Unit4 voorkomen */
  next: z.array(CatalogSeriesNextSchema),
  /** De prefix is zelf een bestaande Unit4-code → dat artikel (anders null) */
  exact: CatalogSeriesExactSchema.nullable(),
  /** De prefix is al de code van een Klingerator-product (anders null) */
  local: CatalogSeriesLocalSchema.nullable(),
});
export type CatalogSeries = z.infer<typeof CatalogSeriesSchema>;
